import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  MapPin,
  CheckCircle,
  Globe,
  Wallet,
  MessageCircle,
  BookOpen,
  LogOut,
  User,
  ChevronDown,
  Plane,
  Home,
  ListTodo,
  Building2,
  Users,
  Shield,
  Menu,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(user?.mode || "preparing");
  const [targetCountry, setTargetCountry] = useState(user?.target_country || "");
  const [tasks, setTasks] = useState([]);
  const [budgetItems, setBudgetItems] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [conversations, setConversations] = useState([]);

  useEffect(() => {
    fetchTasks();
    fetchBudget();
    if (user?.role === "newbie" || user?.role === "local_homey") {
      fetchConversations();
    }
  }, [user]);

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${API}/tasks`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  const fetchBudget = async () => {
    try {
      const response = await fetch(`${API}/budget`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setBudgetItems(data);
      }
    } catch (error) {
      console.error("Error fetching budget:", error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API}/conversations`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  const updateMode = async (newMode) => {
    try {
      const response = await fetch(`${API}/users/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ mode: newMode }),
      });
      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        setMode(newMode);
        toast.success(`Switched to ${newMode === "preparing" ? "Preparing to Move" : "Already Landed"} mode`);
      }
    } catch (error) {
      toast.error("Failed to update mode");
    }
  };

  const updateCountry = async (country) => {
    try {
      const response = await fetch(`${API}/users/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ target_country: country }),
      });
      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        setTargetCountry(country);
        toast.success(`Target country set to ${country.charAt(0).toUpperCase() + country.slice(1)}`);
      }
    } catch (error) {
      toast.error("Failed to update country");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      localStorage.removeItem("lh_session_token");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      localStorage.removeItem("lh_session_token");
      navigate("/", { replace: true });
    }
  };

  const completedTasks = tasks.filter((t) => t.completed).length;
  const totalTasks = tasks.length;
  const taskProgress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  const totalBudget = budgetItems.reduce((sum, item) => {
    if (item.frequency === "monthly") return sum + item.amount;
    if (item.frequency === "yearly") return sum + item.amount / 12;
    return sum;
  }, 0);

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";
  };

  const sidebarItems = [
    { icon: Home, label: "Dashboard", path: "/dashboard", active: true },
    { icon: ListTodo, label: "Tasks", path: "/tasks" },
    { icon: Globe, label: "Country Guides", path: "/guides" },
    { icon: Wallet, label: "Budget Planner", path: "/budget" },
    ...(mode === "landed"
      ? [
          { icon: Building2, label: "Local Services", path: "/services" },
          { icon: BookOpen, label: "Cultural Guide", path: "/culture" },
        ]
      : []),
    { icon: MessageCircle, label: "Chat", path: "/chat" },
    { icon: User, label: "Profile", path: "/profile" },
    ...(user?.role === "admin" ? [{ icon: Shield, label: "Admin", path: "/admin" }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#F5F5F0] border-r border-[#E2E8F0] h-screen sticky top-0">
        <div className="p-6 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#4A6C6F] rounded-full flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-semibold text-[#1A202C]">Local Homey</span>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {sidebarItems.map((item) => (
            <button
              key={item.path}
              data-testid={`sidebar-${item.label.toLowerCase().replace(" ", "-")}`}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                item.active
                  ? "bg-[#4A6C6F]/15 text-[#4A6C6F] border-l-3 border-[#4A6C6F]"
                  : "text-[#4A5568] hover:bg-[#4A6C6F]/10"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-[#E2E8F0]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-btn"
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#4A6C6F]/10 transition-colors"
              >
                <Avatar className="w-10 h-10">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-[#4A6C6F] text-white">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="font-medium text-[#1A202C] text-sm truncate">{user?.name}</p>
                  <p className="text-xs text-[#718096] capitalize">{user?.role}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-[#718096]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate("/profile")}>
                <User className="mr-2 w-4 h-4" /> Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="mr-2 w-4 h-4" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-[#F5F5F0] shadow-xl">
            <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#4A6C6F] rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading text-xl font-semibold">Local Homey</span>
              </div>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="p-4 space-y-1">
              {sidebarItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                    item.active
                      ? "bg-[#4A6C6F]/15 text-[#4A6C6F]"
                      : "text-[#4A5568] hover:bg-[#4A6C6F]/10"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0] px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                data-testid="mobile-menu-toggle"
                className="lg:hidden p-2"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              
              {/* Mode Toggle */}
              <div className="flex bg-[#F5F5F0] rounded-full p-1">
                <button
                  data-testid="mode-preparing"
                  onClick={() => updateMode("preparing")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                    mode === "preparing"
                      ? "bg-[#4A6C6F] text-white"
                      : "text-[#4A5568] hover:text-[#1A202C]"
                  }`}
                >
                  <Plane className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm font-medium">Preparing</span>
                </button>
                <button
                  data-testid="mode-landed"
                  onClick={() => updateMode("landed")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                    mode === "landed"
                      ? "bg-[#4A6C6F] text-white"
                      : "text-[#4A5568] hover:text-[#1A202C]"
                  }`}
                >
                  <Home className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm font-medium">Landed</span>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Select value={targetCountry} onValueChange={updateCountry}>
                <SelectTrigger data-testid="country-select" className="w-40 bg-white border-[#E2E8F0]">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ireland">🇮🇪 Ireland</SelectItem>
                  <SelectItem value="uk">🇬🇧 United Kingdom</SelectItem>
                  <SelectItem value="canada">🇨🇦 Canada</SelectItem>
                </SelectContent>
              </Select>

              <div className="hidden lg:block">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Avatar className="w-10 h-10 cursor-pointer">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-[#4A6C6F] text-white">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate("/profile")}>
                      <User className="mr-2 w-4 h-4" /> Profile
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut className="mr-2 w-4 h-4" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="p-4 lg:p-8">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="font-heading text-2xl lg:text-3xl font-semibold text-[#1A202C] mb-2">
              Welcome back, {user?.name?.split(" ")[0]}!
            </h1>
            <p className="text-[#4A5568]">
              {mode === "preparing"
                ? "Let's prepare for your big move. Here's what you need to focus on."
                : "You've landed! Here's help to settle in your new home."}
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card data-testid="stat-tasks" className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-[#4A6C6F]/10 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-[#4A6C6F]" />
                  </div>
                  <span className="text-2xl font-bold text-[#1A202C]">
                    {completedTasks}/{totalTasks}
                  </span>
                </div>
                <p className="text-[#4A5568] text-sm mb-2">Tasks Completed</p>
                <Progress value={taskProgress} className="h-2" />
              </CardContent>
            </Card>

            <Card data-testid="stat-budget" className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-[#D97757]/10 rounded-xl flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-[#D97757]" />
                  </div>
                  <span className="text-2xl font-bold text-[#1A202C]">
                    €{totalBudget.toFixed(0)}
                  </span>
                </div>
                <p className="text-[#4A5568] text-sm">Monthly Budget</p>
                <p className="text-xs text-[#718096] mt-1">{budgetItems.length} items tracked</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-country" className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-[#48BB78]/10 rounded-xl flex items-center justify-center">
                    <Globe className="w-6 h-6 text-[#48BB78]" />
                  </div>
                  <span className="text-2xl">
                    {targetCountry === "ireland" && "🇮🇪"}
                    {targetCountry === "uk" && "🇬🇧"}
                    {targetCountry === "canada" && "🇨🇦"}
                    {!targetCountry && "🌍"}
                  </span>
                </div>
                <p className="text-[#4A5568] text-sm">Target Country</p>
                <p className="text-xs text-[#718096] mt-1 capitalize">{targetCountry || "Not selected"}</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-chat" className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-[#4299E1]/10 rounded-xl flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-[#4299E1]" />
                  </div>
                  <span className="text-2xl font-bold text-[#1A202C]">{conversations.length}</span>
                </div>
                <p className="text-[#4A5568] text-sm">Active Chats</p>
                <p className="text-xs text-[#718096] mt-1">
                  {user?.role === "local_homey" ? "Helping newbies" : "With Local Homeys"}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {mode === "preparing" ? (
              <>
                <Card
                  data-testid="action-tasks"
                  className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl cursor-pointer hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1)] transition-shadow"
                  onClick={() => navigate("/tasks")}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-[#1A202C]">
                      <ListTodo className="w-5 h-5 text-[#4A6C6F]" />
                      Moving Checklist
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#4A5568] mb-4">
                      Track all your pre-move tasks from visa applications to packing.
                    </p>
                    <div className="flex items-center gap-2">
                      <Progress value={taskProgress} className="flex-1 h-2" />
                      <span className="text-sm text-[#718096]">{Math.round(taskProgress)}%</span>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  data-testid="action-guides"
                  className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl cursor-pointer hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1)] transition-shadow"
                  onClick={() => navigate("/guides")}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-[#1A202C]">
                      <Globe className="w-5 h-5 text-[#4A6C6F]" />
                      Country Guides
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#4A5568] mb-4">
                      Everything you need to know about your destination country.
                    </p>
                    <div className="flex gap-2">
                      <span className="px-3 py-1 bg-[#F5F5F0] rounded-full text-sm">🇮🇪 Ireland</span>
                      <span className="px-3 py-1 bg-[#F5F5F0] rounded-full text-sm">🇬🇧 UK</span>
                      <span className="px-3 py-1 bg-[#F5F5F0] rounded-full text-sm">🇨🇦 Canada</span>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                <Card
                  data-testid="action-services"
                  className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl cursor-pointer hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1)] transition-shadow"
                  onClick={() => navigate("/services")}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-[#1A202C]">
                      <Building2 className="w-5 h-5 text-[#4A6C6F]" />
                      Local Services
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#4A5568] mb-4">
                      Find banks, healthcare, housing, and utilities in your area.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <span className="px-3 py-1 bg-[#4A6C6F]/10 text-[#4A6C6F] rounded-full text-sm">Banking</span>
                      <span className="px-3 py-1 bg-[#4A6C6F]/10 text-[#4A6C6F] rounded-full text-sm">Healthcare</span>
                      <span className="px-3 py-1 bg-[#4A6C6F]/10 text-[#4A6C6F] rounded-full text-sm">Housing</span>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  data-testid="action-culture"
                  className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl cursor-pointer hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1)] transition-shadow"
                  onClick={() => navigate("/culture")}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-[#1A202C]">
                      <BookOpen className="w-5 h-5 text-[#4A6C6F]" />
                      Cultural Integration
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#4A5568] mb-4">
                      Learn local customs, etiquette, and tips to feel at home faster.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <span className="px-3 py-1 bg-[#D97757]/10 text-[#D97757] rounded-full text-sm">Greetings</span>
                      <span className="px-3 py-1 bg-[#D97757]/10 text-[#D97757] rounded-full text-sm">Work Culture</span>
                      <span className="px-3 py-1 bg-[#D97757]/10 text-[#D97757] rounded-full text-sm">Customs</span>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Connect with Local Homey */}
          {user?.role === "newbie" && (
            <Card data-testid="connect-homey-card" className="bg-[#4A6C6F] border-0 rounded-2xl text-white">
              <CardContent className="p-6 lg:p-8">
                <div className="flex flex-col lg:flex-row items-center gap-6">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <Users className="w-8 h-8" />
                  </div>
                  <div className="flex-1 text-center lg:text-left">
                    <h3 className="font-heading text-xl font-semibold mb-2">
                      Get matched with a Local Homey
                    </h3>
                    <p className="text-white/80">
                      Connect with someone already living in {targetCountry || "your destination"} who can answer all your questions.
                    </p>
                  </div>
                  <Button
                    data-testid="find-homey-btn"
                    onClick={() => navigate("/chat")}
                    className="bg-white text-[#4A6C6F] hover:bg-white/90 rounded-full px-6"
                  >
                    Find a Local Homey
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Local Homey CTA */}
          {user?.role === "newbie" && !user?.local_homey_status && (
            <Card data-testid="become-homey-card" className="mt-6 bg-[#F5F5F0] border-0 rounded-2xl">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="w-12 h-12 bg-[#D97757]/20 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#D97757]" />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <h4 className="font-medium text-[#1A202C]">Already settled in your new country?</h4>
                    <p className="text-sm text-[#4A5568]">Apply to become a Local Homey and help others!</p>
                  </div>
                  <Button
                    data-testid="apply-homey-btn"
                    onClick={() => navigate("/profile")}
                    variant="outline"
                    className="border-[#D97757] text-[#D97757] hover:bg-[#D97757]/10 rounded-full"
                  >
                    Apply Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
